import { DEFAULT_PASSWORD_POLICY, type PasswordPolicy } from '@/utils/validation'

/**
 * The password policy the server actually enforces.
 *
 * Every form that asks for a new password has to validate against this and not
 * against a second set of rules of its own, or the form accepts something the
 * server then rejects and the person sees a generic failure with no rule named.
 *
 * The answer is a deployment constant, so it is fetched once and shared. A
 * failed fetch falls back to the defaults rather than blocking the form: the
 * server still has the final say, and a slightly wrong hint beats a dead form.
 */

interface PasswordPolicyResponse {
  min_length: number
  max_length: number
  require_letter: boolean
  require_digit: boolean
  blocks_common_passwords?: boolean
  expires_after_days?: number
}

let cached: PasswordPolicy | null = null
let inFlight: Promise<PasswordPolicy> | null = null

export async function getPasswordPolicy(): Promise<PasswordPolicy> {
  if (cached) return cached
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const response = await fetch('/api/v2/auth/password-policy', {
        credentials: 'include',
      })
      if (!response.ok) return DEFAULT_PASSWORD_POLICY

      const data = (await response.json()) as PasswordPolicyResponse
      const policy: PasswordPolicy = {
        minLength: data.min_length ?? DEFAULT_PASSWORD_POLICY.minLength,
        maxLength: data.max_length ?? DEFAULT_PASSWORD_POLICY.maxLength,
        requireLetter: data.require_letter ?? DEFAULT_PASSWORD_POLICY.requireLetter,
        requireDigit: data.require_digit ?? DEFAULT_PASSWORD_POLICY.requireDigit,
        blocksCommonPasswords:
          data.blocks_common_passwords ?? DEFAULT_PASSWORD_POLICY.blocksCommonPasswords,
        expiresAfterDays: data.expires_after_days ?? DEFAULT_PASSWORD_POLICY.expiresAfterDays,
      }
      cached = policy
      return policy
    } catch {
      return DEFAULT_PASSWORD_POLICY
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/** Drops the cached policy. Used by tests and after a config reload. */
export function resetPasswordPolicyCache(): void {
  cached = null
  inFlight = null
}
